/*
 * <jj-slime-options>
 *
 * Slime & Squishy Bar booking options. See snippets/jj-slime-options.liquid
 * for the pricing model. In short: additional children, cart customization,
 * the activity table and chairs are REAL cart lines (so they are inside the
 * estimated rental total), and the hosted service is a property only (so the
 * estimate says plainly that it is not included).
 *
 * NO PRICES OR VARIANT IDS LIVE IN THIS FILE. They are rendered by Liquid into
 * [data-jj-sl-data] from real Shopify variants.
 *
 * Patterns carried over from jj-bundle-machines.js / jj-bumper-options.js:
 *   - variant changes arrive via PUB_SUB_EVENTS.variantChange (product-info
 *     patches in place, it does not re-render this block);
 *   - aria-disabled is the ONLY thing written on the submit button;
 *   - this element is the single submitter on this template. With no paid
 *     extras it does not intercept at all and the stock submit carries the
 *     property inputs. With extras it posts one atomic multi-item
 *     /cart/add.js, reading JJCartExtras for any other provider on the page;
 *   - FAILS OPEN: if init throws, the page behaves like a normal product page.
 */

(function () {
  'use strict';

  if (customElements.get('jj-slime-options')) return;

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function money(amount) {
    var rounded = Math.round(amount * 100) / 100;
    return '$' + (rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2));
  }

  function makeLineKey() {
    return 'ssb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  customElements.define(
    'jj-slime-options',
    class JjSlimeOptions extends HTMLElement {
      constructor() {
        super();
        this.onInput = this.onInput.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
      }

      connectedCallback() {
        try {
          this.submitting = false;
          this.data = { packages: {} };

          var node = this.querySelector('[data-jj-sl-data]');
          if (node) {
            try {
              this.data = JSON.parse(node.textContent);
            } catch (error) {
              console.error('Slime options: data unreadable', error);
            }
          }

          this.addEventListener('change', this.onInput);
          this.addEventListener('input', this.onInput);
          this.addEventListener('click', this.onClick);
          document.addEventListener('submit', this.onSubmitCapture, true);

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
          }

          this.cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.variantId = this.readVariantId();
          this.refresh();
        } catch (error) {
          console.error('Slime options: init failed', error);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onInput);
        this.removeEventListener('input', this.onInput);
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('submit', this.onSubmitCapture, true);
        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
        try {
          this.setBlocked(false);
        } catch (error) {
          /* ignore */
        }
      }

      /* ---------------- Lookups ---------------- */

      get productInfo() {
        return this.closest('product-info');
      }

      get formElement() {
        var id = this.dataset.formId;
        var byId = id ? document.getElementById(id) : null;
        if (byId) return byId;
        return this.productInfo ? this.productInfo.querySelector('product-form form') : null;
      }

      get submitButton() {
        var byId = document.getElementById(this.dataset.submitId);
        if (byId) return byId;
        return this.productInfo ? this.productInfo.querySelector('product-form [type="submit"]') : null;
      }

      q(selector) {
        return this.querySelector(selector);
      }

      prop(key) {
        return this.q('[data-jj-sl-prop="' + key + '"]');
      }

      readVariantId() {
        var form = this.formElement;
        var input = form ? form.querySelector('input[name="id"]') : null;
        if (input && input.value) return String(input.value);

        var selected = this.productInfo ? this.productInfo.querySelector('variant-selects [data-selected-variant]') : null;
        if (selected) {
          try {
            var parsed = JSON.parse(selected.innerHTML);
            return parsed && parsed.id ? String(parsed.id) : '';
          } catch (error) {
            return '';
          }
        }
        return '';
      }

      onVariantChange(event) {
        var data = event && event.data;
        if (!data) return;
        var productInfo = this.productInfo;
        var ownSectionId = productInfo ? productInfo.dataset.originalSection || productInfo.dataset.section : null;
        if (data.sectionId && ownSectionId && data.sectionId !== ownSectionId) return;
        this.variantId = data.variant ? String(data.variant.id) : '';
        this.refresh();
      }

      /* ---------------- State ---------------- */

      get pkg() {
        return (this.data.packages || {})[this.variantId] || null;
      }

      get isTwenty() {
        return !!this.variantId && this.variantId === String(this.data.twentyVariant || '');
      }

      childCount() {
        var input = this.q('[data-jj-sl-children]');
        if (!input || !this.data.child || !this.isTwenty) return 0;
        return clamp(toInt(input.value, 0), 0, toInt(input.max, 80));
      }

      chairCount() {
        var input = this.q('[data-jj-sl-chairs]');
        if (!input || !this.data.chair) return 0;
        return clamp(toInt(input.value, 0), 0, toInt(input.max, 100));
      }

      service() {
        var checked = this.q('[data-jj-sl-service]:checked');
        return checked ? checked.value : 'self';
      }

      customOn() {
        var toggle = this.q('[data-jj-sl-custom-toggle]');
        return !!(toggle && toggle.checked && this.data.custom);
      }

      customText() {
        var input = this.q('[data-jj-sl-custom-text]');
        return input ? input.value.trim() : '';
      }

      table() {
        var checked = this.q('[data-jj-sl-table]:checked');
        var value = checked ? checked.value : 'none';
        return value === 'table6' || value === 'table8' ? (this.data[value] ? value : 'none') : 'none';
      }

      /* ---------------- Events ---------------- */

      onClick(event) {
        var button = event.target.closest ? event.target.closest('[data-jj-sl-step]') : null;
        if (!button) return;
        var input = button.dataset.jjSlStep === 'children' ? this.q('[data-jj-sl-children]') : this.q('[data-jj-sl-chairs]');
        if (!input) return;
        var next = toInt(input.value, 0) + toInt(button.dataset.dir, 0);
        input.value = String(clamp(next, 0, toInt(input.max, 100)));
        this.refresh();
      }

      onInput(event) {
        var target = event.target;
        if (!target || !target.matches) return;
        if (target.matches('[data-jj-sl-children], [data-jj-sl-chairs]') && event.type === 'change') {
          target.value = String(clamp(toInt(target.value, 0), 0, toInt(target.max, 100)));
        }
        if (target.matches('[data-jj-sl-custom-text]')) target.removeAttribute('aria-invalid');
        this.refresh();
      }

      /* ---------------- Rendering ---------------- */

      refresh() {
        // Additional children only exist on the 20-kid package. Leaving it
        // resets the count, so a hidden quantity can never be submitted.
        var childOn = this.q('[data-jj-sl-children-on]');
        var childOff = this.q('[data-jj-sl-children-off]');
        var childInput = this.q('[data-jj-sl-children]');
        if (childOn) childOn.hidden = !this.isTwenty;
        if (childOff) childOff.hidden = this.isTwenty;
        if (childInput && !this.isTwenty) childInput.value = '0';

        var customWrap = this.q('[data-jj-sl-custom-wrap]');
        var customInput = this.q('[data-jj-sl-custom-text]');
        var customOn = this.customOn();
        if (customWrap) customWrap.hidden = !customOn;
        if (customInput) {
          customInput.disabled = !customOn;
          if (!customOn) {
            customInput.value = '';
            customInput.removeAttribute('aria-invalid');
          }
        }

        this.writeProps();
        this.renderTotal();
        this.setBlocked(customOn && !this.customText());
      }

      lines() {
        var rows = [];
        var pkg = this.pkg;
        if (pkg) rows.push({ label: 'Package: ' + pkg.title, amount: pkg.price });

        var children = this.childCount();
        if (children > 0) rows.push({ label: children + ' additional ' + (children === 1 ? 'child' : 'children') + ' × ' + money(this.data.child.price), amount: children * this.data.child.price });

        if (this.customOn()) rows.push({ label: 'Cart customization', amount: this.data.custom.price });

        var table = this.table();
        if (table !== 'none') rows.push({ label: (table === 'table6' ? '6-ft' : '8-ft') + ' activity table', amount: this.data[table].price });

        var chairs = this.chairCount();
        if (chairs > 0) rows.push({ label: chairs + (chairs === 1 ? ' chair' : ' chairs') + ' × ' + money(this.data.chair.price), amount: chairs * this.data.chair.price });

        return rows;
      }

      renderTotal() {
        var rowsEl = this.q('[data-jj-sl-rows]');
        var totalEl = this.q('[data-jj-sl-total]');
        var excludesEl = this.q('[data-jj-sl-excludes]');
        var rows = this.lines();
        var total = 0;

        if (rowsEl) {
          rowsEl.innerHTML = '';
          rows.forEach(function (row) {
            total += row.amount;
            var div = document.createElement('div');
            div.className = 'jj-sl__row';
            var label = document.createElement('span');
            label.textContent = row.label;
            var amount = document.createElement('span');
            amount.textContent = money(row.amount);
            div.appendChild(label);
            div.appendChild(amount);
            rowsEl.appendChild(div);
          });
        } else {
          rows.forEach(function (row) {
            total += row.amount;
          });
        }

        if (totalEl) totalEl.textContent = money(total);

        if (excludesEl) {
          if (this.service() === 'hosted' && this.data.hostPrice) {
            excludesEl.textContent =
              'Excludes delivery/pickup (quoted separately by location) and the ' +
              money(this.data.hostPrice) +
              ' one-hour hosted service, which is added to your final invoice.';
          } else {
            excludesEl.textContent = 'Excludes delivery/pickup, which depends on your location and is quoted separately.';
          }
        }
      }

      setProp(key, value) {
        var input = this.prop(key);
        if (!input) return;
        input.value = value || '';
        input.disabled = !value;
      }

      writeProps() {
        if (this.service() === 'hosted' && this.data.hostPrice) {
          this.setProp('service', 'One-hour hosted service (+' + money(this.data.hostPrice) + ', added to final invoice, not in estimated total)');
        } else {
          this.setProp('service', 'Self-serve (adult supervision required)');
        }

        var children = this.childCount();
        this.setProp('children', children > 0 ? String(children) + ' (+' + money(children * this.data.child.price) + ')' : '');

        this.setProp('custom', this.customOn() && this.customText() ? this.customText() : '');

        var table = this.table();
        this.setProp('table', table === 'table6' ? '6-ft table' : table === 'table8' ? '8-ft table' : 'None');

        var chairs = this.chairCount();
        this.setProp('chairs', chairs > 0 ? String(chairs) : 'None');
      }

      /* ---------------- Extra cart lines ---------------- */

      extraItems(lineKey) {
        var items = [];
        var children = this.childCount();
        if (children > 0) {
          items.push({
            id: this.data.child.id,
            quantity: children,
            properties: { _package_line_key: lineKey, _upgrade_display: 'Slime & Squishy Bar — additional children' }
          });
        }
        if (this.customOn()) {
          items.push({
            id: this.data.custom.id,
            quantity: 1,
            properties: { _package_line_key: lineKey, _upgrade_display: 'Slime & Squishy Bar — cart customization' }
          });
        }
        var table = this.table();
        if (table !== 'none') {
          items.push({
            id: this.data[table].id,
            quantity: 1,
            properties: {
              _package_line_key: lineKey,
              _upgrade_display: 'Slime & Squishy Bar — ' + (table === 'table6' ? '6-ft' : '8-ft') + ' activity table'
            }
          });
        }
        var chairs = this.chairCount();
        if (chairs > 0) {
          items.push({
            id: this.data.chair.id,
            quantity: chairs,
            properties: { _package_line_key: lineKey, _upgrade_display: 'White folding chairs (Slime & Squishy Bar)' }
          });
        }
        return items;
      }

      resetExtras() {
        var children = this.q('[data-jj-sl-children]');
        var chairs = this.q('[data-jj-sl-chairs]');
        var toggle = this.q('[data-jj-sl-custom-toggle]');
        var none = this.q('[data-jj-sl-table][value="none"]');
        if (children) children.value = '0';
        if (chairs) chairs.value = '0';
        if (toggle) toggle.checked = false;
        if (none) none.checked = true;
        this.setProp('linekey', '');
        this.refresh();
      }

      /* ---------------- Submit ---------------- */

      onSubmitCapture(event) {
        var form = this.formElement;
        if (!form || event.target !== form) return;

        if (this.customOn() && !this.customText()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          var input = this.q('[data-jj-sl-custom-text]');
          if (input) {
            input.setAttribute('aria-invalid', 'true');
            input.focus({ preventScroll: true });
          }
          this.setStatus('Tell us the name, wording, or theme for the cart, or untick cart customization.', 'error');
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        this.writeProps();

        var registry = window.JJCartExtras || null;
        var others = registry ? registry.items('__jj_probe__') : [];
        var mine = this.extraItems('__jj_probe__');

        // Nothing extra: stock submit, carrying the property inputs.
        if (!mine.length && !others.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.submitting) return;
        this.submitting = true;

        var self = this;
        var lineKey = makeLineKey();
        this.setProp('linekey', lineKey);

        var data = new FormData(form);
        var properties = {};
        data.forEach(function (value, key) {
          var match = /^properties\[(.+)\]$/.exec(key);
          if (!match || String(value).trim() === '') return;
          properties[match[1]] = value;
        });

        var mainItem = { id: data.get('id'), quantity: 1, properties: properties };
        var items = [mainItem].concat(this.extraItems(lineKey), registry ? registry.items(lineKey) : []);
        var body = { items: items };

        if (this.cartElement && typeof this.cartElement.getSectionsToRender === 'function') {
          body.sections = this.cartElement
            .getSectionsToRender()
            .map(function (section) {
              return section.id;
            })
            .join(',');
          body.sections_url = window.location.pathname;
          if (typeof this.cartElement.setActiveElement === 'function') this.cartElement.setActiveElement(document.activeElement);
        }

        this.lockButton(true);

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (payload) {
            if (payload.status) {
              self.setStatus(payload.description || payload.message || 'We could not add this to your event. Please try again.', 'error');
              return;
            }

            self.resetExtras();
            if (registry && typeof registry.reset === 'function') registry.reset();

            if (!self.cartElement) {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }

            self.cartElement.renderContents(payload);
            if (self.cartElement.classList.contains('is-empty')) self.cartElement.classList.remove('is-empty');

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jj-slime-options', productVariantId: mainItem.id, cartData: payload });
            }
          })
          .catch(function (error) {
            console.error('Slime options: add failed', error);
            self.setStatus('Something went wrong adding this to your event. Please try again.', 'error');
          })
          .finally(function () {
            self.submitting = false;
            self.lockButton(false);
            self.refresh();
          });
      }

      /* ---------------- Button + status ---------------- */

      setBlocked(blocked) {
        var button = this.submitButton;
        if (!button) return;
        if (blocked) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('jj-sl-blocked');
        } else {
          button.removeAttribute('aria-disabled');
          button.classList.remove('jj-sl-blocked');
          var status = this.q('[data-jj-sl-status]');
          if (status && status.dataset.state === 'error') this.setStatus('');
        }
      }

      lockButton(locked) {
        var button = this.submitButton;
        if (!button) return;
        var spinner = button.querySelector('.loading__spinner');
        if (locked) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      setStatus(message, state) {
        var element = this.q('[data-jj-sl-status]');
        if (!element) return;
        element.textContent = message;
        element.dataset.state = state || '';
      }
    }
  );
})();
